import {resolveBackground} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {
    generateInfoNotification,
    generateWarningNotification,
} from '#core/application/modules/tiles/notification/index.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {generateBetFundsInfo, BET_FUNDS_INFO_TYPES} from '#core/application/modules/tiles/bet-funds-info/index.js';
import {generateAnimatedButton, generateSecondaryButton} from '#core/application/modules/tiles/button/index.js';

import {Betslip as BetslipFeature} from '../betslip/betslip.js';

export function overaskFactory(PALETTE) {
    const {FONT} = PALETTE;

    const OVERASK_BET_OFFER_BASE_INFO = generateBetBaseInfo(PALETTE.OVERASK_BET_OFFER_BASE_INFO);
    const OVERASK_BET_OFFER_BET_FUNDS_INFO = {
        ...OVERASK_BET_OFFER_BASE_INFO.betFundsInfo.medium,
        changedValueIndicator: merge(OVERASK_BET_OFFER_BASE_INFO.oddsChangedValue, {
            newValueContainer: {
                style: {
                    padding: 1,
                    fontWeight: FONT.WEIGHT_NORMAL,
                },
            },
        }),
    };

    const OVERASK_BET_OFFER_TOTAL_INFO_NORMAL = generateBetFundsInfo(PALETTE.OVERASK_BET_OFFER_TOTAL_INFO);
    const OVERASK_BET_OFFER_TOTAL_INFO_EMPHASIZED = generateBetFundsInfo(
        PALETTE.OVERASK_BET_OFFER_TOTAL_INFO,
        BET_FUNDS_INFO_TYPES.EMPHASIZED
    );
    const OVERASK_BET_OFFER_BONUS_PROMOTIONS_NOTIFICATION = merge(
        generateInfoNotification(PALETTE.OVERASK_BET_OFFER_BONUS_PROMOTIONS_NOTIFICATION),
        {
            text: {
                container: {
                    padding: '0 0 0 8px',
                },
            },
        }
    );

    return {
        OveraskView: {
            // reffered
            referredContainer: {
                style: {
                    ...resolveBackground(PALETTE.OVERASK_REVIEWING_CONTAINER_BG),
                    textAlign: 'center',
                    padding: 8,
                },
            },
            referredLabel: {
                style: {
                    fontSize: '13px',
                    ...PALETTE.OVERASK_REVIEWING_TEXT,
                },
            },
            spinner: {
                image: {
                    style: {
                        fill: PALETTE.OVERASK_REVIEWING_SPINNER_ICON_COLOR,
                    },
                },
            },
            // common
            container: {
                style: {
                    ...resolveBackground(PALETTE.OVERASK_CONTAINER_BG),
                    _mobile_height: 'auto',
                    _mobile_minHeight: '100%',
                },
            },
            betOffersAndStatusContainer: {
                style: {
                    ...resolveBackground(PALETTE.OVERASK_BET_OFFER_CONTAINER_BG),
                },
            },
            style: {
                offeredBetsBackgroundColor: PALETTE.OVERASK_TRADER_BET_OFFER_CONTAINER_BG,
            },
            headerContainer: {
                style: {
                    padding: 8,
                },
            },
            buttonsContainer: {
                style: {
                    padding: '0 8px 8px',
                },
            },
            totalFundsContainer: {
                style: {
                    padding: 8,
                },
            },
            headerLabel: generateWarningNotification(PALETTE.OVERASK_TRADER_CHANGES_NOTIFICATION),
            timer: {
                timerWrapper: {
                    style: {
                        fontSize: FONT.SIZE_SMALL,
                        fontWeight: FONT.WEIGHT_BOLD,
                        ...PALETTE.OVERASK_TIMER_TEXT,
                    },
                },
            },
            // potentially we can have problems here because this label is rendered on betOffersAndStatusContainer bg or
            // offeredBetsBackgroundColor, but these two containers can have different bg colors, but for label we have only one config
            // without splitting it by bet offer status
            betOffersStatusLabel: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    fontWeight: FONT.WEIGHT_BOLD,
                    textTransform: 'uppercase',
                    ...PALETTE.OVERASK_BET_OFFER_STATUS_TITLE_TEXT,
                },
            },
            betOffersContainer: {
                style: {
                    padding: '8px 0 0 0',
                },
            },
            betOffer: {
                container: {
                    style: {
                        borderRadius: PALETTE.OVERASK_BET_OFFER_BORDER_RADIUS,
                        ...resolveBackground(PALETTE.OVERASK_BET_OFFER_BG),
                        boxShadow: PALETTE.OVERASK_BET_OFFER_SHADOW,
                        padding: 0,
                        overflow: 'hidden',
                    },
                },
                baseOddsContainer: {
                    style: {
                        borderRadius: PALETTE.OVERASK_BET_OFFER_BORDER_RADIUS,
                        ...resolveBackground(PALETTE.OVERASK_BET_OFFER_BG),
                        boxShadow: PALETTE.OVERASK_BET_OFFER_SHADOW,
                        padding: 0,
                        overflow: 'hidden',
                    },
                },
                headerContainer: {
                    style: {
                        padding: 8,
                        ...resolveBackground(PALETTE.OVERASK_BET_OFFER_HEADER_BG),
                    },
                },
                header: {
                    container: {
                        padding: 0,
                    },
                    style: {
                        fontWeight: FONT.WEIGHT_NORMAL,
                        fontSize: FONT.SIZE_MEDIUM,
                        textTransform: 'uppercase',
                        ...PALETTE.OVERASK_BET_OFFER_HEADER_TEXT,
                    },
                },
                checkButton: {
                    style: {
                        base: {
                            position: 'none',
                            minWidth: '20px',
                        },
                    },
                },
                checkButtonImageContainer: {
                    style: {
                        ...resolveBackground(PALETTE.OVERASK_BET_OFFER_HEADER_CHECK_BUTTON_BG),
                        boxSizing: 'border-box',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderRadius: PALETTE.OVERASK_BET_OFFER_HEADER_CHECK_BUTTON_BORDER_RADIUS,
                        borderColor: PALETTE.OVERASK_BET_OFFER_HEADER_CHECK_BUTTON_BORDER_COLOR,
                    },
                },
                checkButtonImage: {
                    style: {
                        fill: PALETTE.OVERASK_BET_OFFER_HEADER_CHECK_BUTTON_ICON_COLOR,
                    },
                },
                totalOdds: merge(OVERASK_BET_OFFER_BASE_INFO.betTotalOdds, {
                    container: {
                        style: {
                            padding: '0 8px 0',
                        },
                    },
                }),
                totalOddsBoost: merge(OVERASK_BET_OFFER_BASE_INFO.betTotalOdds, {
                    container: {
                        style: {
                            padding: '0 8px 0',
                        },
                    },
                }),
                overaskChanged: merge(OVERASK_BET_OFFER_BASE_INFO.betTotalOdds, {
                    container: {
                        style: {
                            padding: '0 8px 0',
                        },
                    },
                }),
                oddsBoostNotification: merge(OVERASK_BET_OFFER_BASE_INFO.oddsBoostLabel, {
                    container: {
                        style: {
                            padding: '0 8px 8px',
                        },
                    },
                }),
                leg: {
                    infoContainer: {
                        style: {
                            padding: 0,
                        },
                    },
                    eventName: {
                        style: OVERASK_BET_OFFER_BASE_INFO.eventName,
                    },
                    odds: {
                        style: OVERASK_BET_OFFER_BASE_INFO.selectionName,
                    },
                    multicastSelectionName: {
                        style: OVERASK_BET_OFFER_BASE_INFO.selectionName,
                    },
                    multicastMarketName: {
                        style: OVERASK_BET_OFFER_BASE_INFO.marketName,
                    },
                    selectionInfoContainer: {
                        style: {
                            padding: 0,
                        },
                    },
                    priceBoostIndicator: merge(OVERASK_BET_OFFER_BASE_INFO.priceBoostValue, {
                        newValueContainer: {
                            style: {
                                padding: 1,
                            },
                        },
                    }),
                    changedOddsIndicator: merge(OVERASK_BET_OFFER_BASE_INFO.oddsChangedValue, {
                        newValueContainer: {
                            style: {
                                padding: 1,
                            },
                        },
                    }),
                    oddsBoostNotification: OVERASK_BET_OFFER_BASE_INFO.oddsBoostLabel,
                    matchAccaSelectionItem: OVERASK_BET_OFFER_BASE_INFO.betBuilderSelectionItem,
                    part: {
                        selectionName: {
                            style: OVERASK_BET_OFFER_BASE_INFO.selectionName,
                        },
                        marketName: {
                            style: {
                                ...OVERASK_BET_OFFER_BASE_INFO.marketName,
                            },
                        },
                        changedMarketIndicator: merge(OVERASK_BET_OFFER_BASE_INFO.oddsChangedValue, {
                            container: {
                                style: {
                                    justifyContent: 'flex-end',
                                    gap: 8,
                                },
                            },
                            previousValue: {
                                style: {
                                    fontSize: FONT.SIZE_SMALL,
                                },
                            },
                            newValueContainer: {
                                style: {
                                    padding: 1,
                                    fontSize: FONT.SIZE_SMALL,
                                },
                            },
                        }),
                    },
                    earlyPayoutIndicator: OVERASK_BET_OFFER_BASE_INFO.earlyPayoutIndicator,
                },
                legsContainer: {
                    style: {
                        padding: '0 16px',
                    },
                },
                fundsContainer: {
                    style: {
                        padding: '0 8px 8px',
                    },
                },
                stakeInfo: {
                    betInfo: OVERASK_BET_OFFER_BET_FUNDS_INFO,
                },
                payoutInfo: {
                    betInfo: OVERASK_BET_OFFER_BET_FUNDS_INFO,
                },
                oddsBoostInfo: {
                    betInfo: OVERASK_BET_OFFER_BET_FUNDS_INFO,
                },
            },
            betBonusPromotions: {
                container: {
                    style: {
                        padding: '0 8px',
                    },
                },
                accaBoostNotification: OVERASK_BET_OFFER_BONUS_PROMOTIONS_NOTIFICATION,
                accaInsuranceNotification: OVERASK_BET_OFFER_BONUS_PROMOTIONS_NOTIFICATION,
                etbNotification: OVERASK_BET_OFFER_BONUS_PROMOTIONS_NOTIFICATION,
            },
            stakePerLineInfo: {
                container: {
                    padding: '8px 8px 0',
                },
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    ...PALETTE.OVERASK_BET_OFFER_STAKE_PER_LINE_TEXT,
                },
            },
            taxDisclaimerWrapper: {
                style: {
                    padding: BetslipFeature.config.showTaxAbsorptionMessage ? '8px 0' : 0,
                },
            },
            totalPayable: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_EMPHASIZED,
            },
            prewager: {
                cash: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
                bonus: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
            },
            freebetStake: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
            },
            cashStake: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
            },
            stakeTax: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
            },
            primaryTotalStake: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_EMPHASIZED,
            },
            secondaryTotalStake: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
            },
            totalPayoutInfo: {
                payout: {
                    betInfo: OVERASK_BET_OFFER_TOTAL_INFO_EMPHASIZED,
                },
                prewager: {
                    cash: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
                    bonus: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
                },
                tax: {
                    betInfo: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
                },
                payoutAfterTax: {
                    betInfo: OVERASK_BET_OFFER_TOTAL_INFO_EMPHASIZED,
                },
            },
            oddsBoostInfo: {
                betInfo: OVERASK_BET_OFFER_TOTAL_INFO_NORMAL,
            },
            rejectButton: generateSecondaryButton(PALETTE.OVERASK_REJECT_OFFER_BUTTON),
            rejectButtonLabel: {
                style: {
                    textAlign: 'center',
                },
            },
            acceptButton: generateAnimatedButton(PALETTE.OVERASK_ACCEPT_OFFER_BUTTON),
            acceptButtonLabel: {
                style: {
                    textAlign: 'center',
                },
            },
        },
    };
}
